import React, { useState } from "react";
import { FaStar, FaPhone, FaMapMarkerAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

export default function DriverDetailsCard({ booking, rawBookingData, onAddReview, onCancel }) {
  const navigate = useNavigate();
  const [driverImageError, setDriverImageError] = useState(false);
  const [carImageError, setCarImageError] = useState(false);

  const rideId = rawBookingData?.bookingId || rawBookingData?._id;
  const isInProgress = rawBookingData?.status === 'inProgress';
  const isCancelled = booking?.status?.toLowerCase() === "cancelled" || rawBookingData?.status?.toLowerCase() === "cancelled";
  const isCompleted = booking?.status?.toLowerCase() === "completed" || rawBookingData?.status?.toLowerCase() === "completed";
  const trackingUrl = rideId ? `/track/${rideId}` : null;

  // Determine which driver to use
  const getActiveDriver = () => {
    const driverSource = rawBookingData?.driverSource;

    if (driverSource === "vendor" && rawBookingData?.vendorDriver) {
      return rawBookingData.vendorDriver;
    }
    if (driverSource === "admin" && rawBookingData?.driver) {
      return rawBookingData.driver;
    }
    if (rawBookingData?.vendorDriver) {
      return rawBookingData.vendorDriver;
    }
    if (rawBookingData?.driver) {
      return rawBookingData.driver;
    }
    return null;
  };

  const driver = getActiveDriver();

  // Format driver name
  const getDriverName = () => {
    if (!driver) return "Driver not assigned yet";
    if (typeof driver === "object") {
      if (driver.name) return driver.name;
      if (driver.firstName || driver.lastName) {
        return `${driver.firstName || ""} ${driver.lastName || ""}`.trim();
      }
    }
    if (typeof driver === "string") {
      if (driver.length === 24 && /^[0-9a-fA-F]{24}$/.test(driver)) {
        return "Driver assigned";
      }
      return driver;
    }
    return "Driver not assigned yet";
  };

  // Get driver contact
  const getDriverContact = () => {
    if (!driver) return "Contact will be provided";
    if (typeof driver === "object") {
      return driver.phone || driver.mobile || driver.phoneNumber || "Contact will be provided";
    }
    return "Contact will be provided";
  };

  // Get driver rating
  const getDriverRating = () => {
    if (!driver || typeof driver !== "object") return 0;
    return driver.ratings || driver.rating || 0;
  };

  // Get vehicle details
  const getVehicleDetails = () => {
    if (!driver || typeof driver !== "object") return "Vehicle details not available";
    if (driver.vehicleModel) {
      return driver.vehicleModel;
    }
    if (driver.vehicleDetails) {
      const make = driver.vehicleDetails.make || "";
      const model = driver.vehicleDetails.model || "";
      return `${make} ${model}`.trim() || "Vehicle details not available";
    }
    return "Vehicle details not available";
  };

  // Get vehicle registration
  const getVehicleRegistration = () => {
    if (!driver || typeof driver !== "object") return "N/A";
    if (driver.vehicleNumber) {
      return driver.vehicleNumber;
    }
    if (driver.vehicleDetails?.registrationNumber) {
      return driver.vehicleDetails.registrationNumber;
    }
    return "N/A";
  };

  // Get driver image
  const getDriverImage = () => {
    if (!driver || typeof driver !== "object") return null;
    return driver.profilePhoto || driver.documents?.driverPhoto || driver.profileImageUrl || driver.imageUrl || driver.profileImage || driver.image || driver.photo || null;
  };

  // Get vehicle image
  const getVehicleImage = () => {
    if (!driver || typeof driver !== "object") return booking?.cabImage || booking?.cabType?.image || "/car.png";
    return driver.documents?.vehiclePhotoFront || driver.documents?.vehiclePhotoBack || booking?.cabImage || booking?.cabType?.image || "/car.png";
  };

  // Get cab type name
  const getCabTypeName = () => {
    if (!booking) return "Vehicle";
    return booking.cabType || "Vehicle";
  };

  const driverName = getDriverName();
  const driverContact = getDriverContact();
  const driverRating = getDriverRating();
  const driverImage = getDriverImage();
  const vehicleImage = getVehicleImage();
  const cabTypeName = getCabTypeName();
  const isDriverAssigned = !driverName.includes("not assigned");
  const userRating = rawBookingData?.userRating;
  const hasUserRated = userRating && userRating.rating;

  // Render stars
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    return (
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, i) => (
          <FaStar
            key={i}
            size={16}
            className={
              i < fullStars
                ? "text-orange-400"
                : i === fullStars && hasHalfStar
                ? "text-orange-200"
                : "text-gray-300"
            }
          />
        ))}
      </div>
    );
  };

  return (
    <div className="w-full lg:w-[320px] lg:ml-auto bg-gradient-to-br from-white to-slate-50/50 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.03)] border border-slate-100/80 p-5 flex flex-col transition-all duration-300 ease-out">
      {/* Title */}
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        Driver Details
      </h2>

      {isCancelled ? (
        <div className="flex flex-col items-center justify-center py-8 px-4 text-center bg-red-50/50 rounded-xl border border-dashed border-red-200/80 my-2 flex-1 w-full">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-500 text-2xl mb-3 border border-red-100 shadow-xs">
            ❌
          </div>
          <h3 className="text-sm font-semibold text-red-800 mb-1">Ride Cancelled</h3>
          <p className="text-xs text-red-500 max-w-[220px] leading-relaxed">
            No driver is assigned because this booking has been cancelled.
          </p>
        </div>
      ) : isDriverAssigned ? (
        <>
          {/* Driver Info */}
          <div className="flex gap-4 items-center mb-4">
            <div>
              {driverImage && !driverImageError ? (
                <img
                  src={driverImage}
                  alt="driver"
                  className="w-16 h-16 rounded-md object-cover"
                  onError={() => setDriverImageError(true)}
                />
              ) : (
                <div className="w-16 h-16 rounded-md bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-xl">
                  {driverName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            <div className="flex-1">
              {/* Rating with number */}
              <div className="flex items-center gap-2 mb-1">
                <div className="flex text-orange-400">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} size={14} className={i < Math.floor(driverRating) ? "text-orange-400" : "text-gray-300"} />
                  ))}
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  {driverRating > 0 ? driverRating.toFixed(1) : "New"}
                </span>
              </div>

              {/* Name */}
              <p className="font-semibold text-gray-900 mb-1">
                {driverName}
              </p>

              {/* Phone */}
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <FaPhone size={12} />
                <a href={`tel:${driverContact}`} className="hover:text-blue-600">
                  {driverContact}
                </a>
              </div>
            </div>
          </div>

          {/* Car Info */}
          <div className="bg-gray-100 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between mb-2">
              {!carImageError ? (
                <img
                  src={vehicleImage}
                  alt="car"
                  className="w-20 h-14 object-contain rounded-md"
                  onError={() => setCarImageError(true)}
                />
              ) : (
                <span className="text-2xl">🚗</span>
              )}

              <div className="text-right">
                <p className="text-xs text-gray-600">Cab Type</p>
                <p className="font-semibold text-gray-900">
                  {cabTypeName}
                </p>
              </div>
            </div>
            <div className="border-t border-gray-300 pt-2">
              <p className="text-xs text-gray-600">License Plate</p>
              <p className="font-semibold text-gray-900">
                {getVehicleRegistration()}
              </p>
            </div>
          </div>

          {/* Buttons or Rating Display */}
          <div className="mt-auto space-y-3">
            {hasUserRated && (
              <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gray-700">Your Rating</p>
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <FaStar
                        key={i}
                        size={14}
                        className={i < userRating.rating ? "text-green-500" : "text-gray-300"}
                      />
                    ))}
                  </div>
                </div>
                {userRating.comment && (
                  <p className="text-sm text-gray-600 italic">"{userRating.comment}"</p>
                )}
              </div>
            )}

            {rawBookingData?.driverRating && rawBookingData.driverRating.rating > 0 && (
              <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gray-700">Driver Rating for You</p>
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <FaStar
                        key={i}
                        size={14}
                        className={i < rawBookingData.driverRating.rating ? "text-yellow-500" : "text-gray-300"}
                      />
                    ))}
                  </div>
                </div>
                {rawBookingData.driverRating.comment && (
                  <p className="text-sm text-gray-600 italic">"{rawBookingData.driverRating.comment}"</p>
                )}
              </div>
            )}

            {/* 🔴 Live Tracking button — shown only when inProgress */}
            {isInProgress && trackingUrl && (
              <a
                href={trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2 rounded-md font-semibold text-sm text-white transition"
                style={{
                  background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                  boxShadow: '0 0 12px rgba(99,102,241,0.45)'
                }}
              >
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: '#10b981', display: 'inline-block',
                  animation: 'pulse 1.5s infinite'
                }} />
                Track Live
                <FaMapMarkerAlt size={12} />
              </a>
            )}

            {!hasUserRated && (isCompleted || booking?.canCancel) && (
              <div className="flex gap-3">
                {isCompleted && (
                  <button
                    onClick={onAddReview}
                    className={`flex-1 bg-blue-100 text-blue-600 py-2 rounded-md font-medium hover:bg-blue-200 transition text-sm ${!booking?.canCancel ? 'w-full' : ''}`}
                  >
                    Add Review
                  </button>
                )}

                {booking?.canCancel && (
                  <button
                    onClick={onCancel}
                    className="flex-1 bg-red-100 text-red-500 py-2 rounded-md font-medium hover:bg-red-200 transition text-sm"
                  >
                    Cancel
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 px-4 text-center bg-gray-50/50 rounded-xl border border-dashed border-gray-200/80 my-2 flex-1">
          <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-500 text-2xl mb-3 border border-blue-100 shadow-xs">
            👤
          </div>
          <h3 className="text-sm font-semibold text-gray-800 mb-1">Driver Not Assigned Yet</h3>
          <p className="text-xs text-gray-500 max-w-[200px] leading-relaxed mb-4">
            A driver will be assigned shortly. Check back soon!
          </p>
          {booking?.canCancel && (
            <button
              onClick={onCancel}
              className="w-full bg-red-100 text-red-500 py-2.5 rounded-xl font-semibold hover:bg-red-200 transition text-sm"
            >
              Cancel Booking
            </button>
          )}
        </div>
      )}
    </div>
  );
}
